// ChatInputBar — the conversation bar at the bottom of the viewport: a text
// field, and the mic that used to float above it.
//
// Andres, 2026-08-07: "we have a visibility problem with the mic recording
// button — integrate it into the same chat text input box like ChatGPT does."
// The mic was a `VoxyWidget` pill absolutely positioned at `bottom: 4rem`,
// centred over the 3D viewport: voxy's own CSS renders its MUTED state at
// `opacity .45; filter: grayscale(.6)` over its own hardcoded dark background,
// and every shipped blueprint sets `startMuted: true` — so the control was a
// faded dark pill on a dark scene precisely when the user was hunting for it.
//
// Two modes, one bar. MUTED (resting) shows the text field. Pressing the mic
// unmutes voxy and the WAVEFORM TAKES OVER THE FIELD — the mode is unmissable
// at full bar width, and it doubles as the "yes, it can hear you" signal that
// the tiny floating canvas never conveyed.
//
// Composition, not the pill: voxy exports `Waveform` and `MuteButton`
// separately from `VoxyWidget`, so this owns the layout while reusing their
// VAD-driven drawing and their mic icons. `MuteButton` alone ships NO css (the
// stylesheet is injected by the `VoxyWidget` constructor, which nothing calls
// any more), so `.voxy-mute-btn` is styled by the app in palette tokens — which
// is also why the mic finally matches a light palette.
//
// Voice does NOT merge with a draft: activating the mic CLEARS the text box
// (Andres' call — voice and text are distinct modes, no hidden buffer). The
// cost is that a stray mic click discards typing with no undo; the button is a
// plain toggle so the way back is one press, but the text is gone.
//
// voxy's own state is the single source of truth for muted-ness — this
// subscribes to `state-change`/`mute-change` rather than tracking it locally,
// so a mute from anywhere (blueprint node, console, echo guard) stays in sync.

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { EventRuntime } from '../lib/EventRuntime'
import { audioPlayer } from '../network/AudioPlayer'
import { devLog } from '../lib/devLog'
import { tracePhase } from '../lib/perfProbe'
import { voxyManager } from '../lib/VoxyManager'
import { resolveVoiceInput } from '../lib/voiceInputState.js'
import { playMicReadyCue } from '../lib/micCue.js'

export default function ChatInputBar({ voxyActive = false, voxyError = null }) {
  const [textInput, setTextInput] = useState('')
  // voxy's raw state ('MUTED' | 'LISTENING' | 'USER_SPEAKING' | ...). Kept whole
  // rather than reduced to a boolean so the bar's glow can react to the user
  // actually speaking, not just to the mic being open.
  const [voxyState, setVoxyState] = useState('MUTED')
  const muted = !voxyActive || voxyState === 'MUTED'
  const isTypingRef = useRef(false)
  const canvasRef = useRef(null)
  const muteHostRef = useRef(null)

  const voice = resolveVoiceInput({ active: voxyActive, error: voxyError, muted })

  // ── Mirror voxy's mute state ─────────────────────────────────────────────
  // Read from VoxyCore rather than assumed from our own clicks: the echo guard,
  // a blueprint node, or __VOXY_MANAGER__ from the console can all change it.
  useEffect(() => {
    if (!voxyActive) { setVoxyState('MUTED'); return }
    const voxy = voxyManager.getVoxy()
    if (!voxy) return
    const sync = () => setVoxyState(voxy.getState())
    sync()
    voxy.on('state-change', sync)
    voxy.on('mute-change', sync)
    return () => {
      voxy.off('state-change', sync)
      voxy.off('mute-change', sync)
    }
  }, [voxyActive])

  // ── Toggle ───────────────────────────────────────────────────────────────
  const toggleVoice = useCallback(() => {
    const cur = resolveVoiceInput({ active: voxyManager.isActive(), error: voxyManager.getLastError(), muted })
    if (cur.disabled) return
    if (cur.listening) {
      devLog('[ChatInputBar] voice → off')
      voxyManager.mute()
      return
    }
    // Going to voice clears the draft. Tell the graph the typing stopped, or a
    // blueprint wired to onUserTyping is left believing the user is mid-word.
    if (isTypingRef.current) {
      isTypingRef.current = false
      EventRuntime.fireEvent('onUserTypingCancelled')
    }
    setTextInput('')
    // The click is a user gesture — the cheapest place to unblock the
    // AudioContext that sentence playback depends on, and it is what lets the
    // cue below actually sound.
    let ctx = null
    try { ctx = audioPlayer.warmup() } catch (err) { devLog('[ChatInputBar] warmup failed:', err) }
    devLog('[ChatInputBar] voice → on')
    voxyManager.unmute()
    // "You can talk now." Fired on user activation rather than from the
    // listening effect, so a remount while already live can't replay it.
    try { playMicReadyCue(ctx) } catch (err) { devLog('[ChatInputBar] mic cue failed:', err) }
  }, [muted])

  // The MuteButton binds its click handler once at construction, so route
  // through a ref to keep it pointed at the latest closure.
  const toggleRef = useRef(toggleVoice)
  useEffect(() => { toggleRef.current = toggleVoice }, [toggleVoice])

  // ── voxy's MuteButton (always mounted, so the mic is never missing) ───────
  const muteBtnRef = useRef(null)
  // `btnReady` exists only so the sync effect below re-runs once the dynamic
  // import lands — the button is created asynchronously and would otherwise
  // miss its first paint.
  const [btnReady, setBtnReady] = useState(false)
  useEffect(() => {
    let cancelled = false
    let btn = null
    import('@avataros/voxy').then(mod => {
      if (cancelled || !muteHostRef.current) return
      btn = new mod.MuteButton(muteHostRef.current, () => toggleRef.current())
      muteBtnRef.current = btn
      setBtnReady(true)
    })
    return () => {
      cancelled = true
      btn?.destroy()
      muteBtnRef.current = null
    }
  }, [])

  useEffect(() => {
    // update(muted, disabled): unavailable renders the struck-through mic,
    // greyed — "voice is off", which is the truth in every disabled case.
    muteBtnRef.current?.update(!voice.listening, voice.disabled)
  }, [voice.listening, voice.disabled, btnReady])

  // ── voxy's Waveform, built only while it owns the field ──────────────────
  useEffect(() => {
    if (!voice.listening) return
    let cancelled = false
    let wf = null
    let voxy = null
    let onState = null
    import('@avataros/voxy').then(mod => {
      voxy = voxyManager.getVoxy()
      if (cancelled || !canvasRef.current || !voxy) return
      wf = new mod.Waveform(voxy, canvasRef.current)
      wf.setState(voxy.getState())
      onState = () => wf?.setState(voxy.getState())
      voxy.on('state-change', onState)
    })
    return () => {
      cancelled = true
      if (voxy && onState) voxy.off('state-change', onState)
      wf?.destroy()
    }
  }, [voice.listening])

  return (
    <div
      data-testid="chat-input-bar"
      className="aos-chat-bar"
      data-voice={voice.listening ? 'listening' : 'idle'}
      // voxy's raw state drives the glow's intensity (see index.css): open mic
      // gets a steady accent edge, and it lifts while you are actually speaking.
      data-voice-state={voxyActive ? voxyState : 'INACTIVE'}
      style={{
        // Studio: the bar floats as a pill above the stage (gass .stu-chatbar)
        // instead of running edge-to-edge. Radius/shadow/border stay in
        // .aos-chat-bar (index.css) with the live-mic glow.
        position: 'absolute', bottom: 10, left: 10, width: 'calc(100% - 20px)',
        display: 'flex', alignItems: 'center',
        background: 'color-mix(in srgb, var(--m-panel) 85%, transparent)',
        // NOTE: border lives in .aos-chat-bar (index.css), NOT here — an
        // inline border would outrank the stylesheet and the live-mic edge
        // could never light up.
        zIndex: 10,
      }}
    >
      {voice.listening ? (
        // The waveform IS the field while listening — full width, same height
        // as the input it replaces, so the bar never changes size.
        <div
          data-testid="chat-voice-waveform"
          style={{ flex: 1, minWidth: 0, height: 38, padding: '0 10px', display: 'flex', alignItems: 'center' }}
        >
          <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: 26 }} />
        </div>
      ) : (
        <input
          data-testid="chat-text-input"
          value={textInput}
          onFocus={() => {
            // Warm the AudioContext on focus — a valid user gesture that
            // happens ~100ms+ before the first keystroke. new AudioContext()
            // costs ~80ms on Chromium; paying it here hides the stall.
            try { audioPlayer.warmup() } catch { /* ignore */ }
          }}
          onChange={e => tracePhase('input.onChange', () => {
            const val = e.target.value
            const wasTyping = isTypingRef.current
            tracePhase('input.setTextInput', () => setTextInput(val))
            if (val && !wasTyping) {
              isTypingRef.current = true
              EventRuntime.fireEvent('onUserTyping')
            }
            if (!val && wasTyping) {
              // User cleared the input after typing — cancel signal
              isTypingRef.current = false
              EventRuntime.fireEvent('onUserTypingCancelled')
            }
          })}
          onKeyDown={e => tracePhase('input.onKeyDown', () => {
            if (e.key === 'Enter' && textInput.trim()) {
              devLog('[TextInput] ↑ ENTER, message =', textInput.trim())
              const cli = window.__CONVORCHER_CLIENT__
              devLog('[TextInput] convorcher client present:', !!cli, '| ws.readyState:', cli?.ws?.readyState, '(1=OPEN)')
              try { audioPlayer.warmup() } catch (err) { console.warn('[TextInput] audioPlayer.warmup error:', err) }
              devLog('[TextInput] firing EventRuntime onTextInput…')
              EventRuntime.fireEvent('onTextInput', { text: textInput.trim() })
              devLog('[TextInput] onTextInput fired')
              tracePhase('input.clearText', () => setTextInput(''))
              isTypingRef.current = false
            }
          })}
          placeholder="Type a message and press Enter..."
          style={{
            flex: 1, minWidth: 0,
            background: 'transparent', border: 'none',
            color: 'var(--m-fg)', padding: '10px 14px',
            fontFamily: 'var(--m-font)', fontSize: 13,
            outline: 'none',
          }}
        />
      )}

      {/* Muted says so out loud, next to the button that fixes it.
          Every shipped blueprint sets startMuted, so this is the state you
          BOOT into: you talk, nothing happens, and nothing on screen connects
          the silence to the mic — the mic just looks broken. A hover title
          cannot carry that, because you have no reason to hover something you
          believe is already on. Clickable, so the hint is also the fix. */}
      {voice.hint && (
        <button
          type="button"
          data-testid="chat-mic-hint"
          onClick={toggleVoice}
          title={voice.title}
          style={{
            flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--m-fg-3)', fontFamily: 'var(--m-font)', fontSize: 11,
            whiteSpace: 'nowrap', padding: '0 2px',
          }}
        >
          {voice.hint}
        </button>
      )}

      {/* Mic. Always in the bar — when voice is unavailable it greys out and
          says why, rather than disappearing (the shell's rail/menu rule). */}
      <div
        ref={muteHostRef}
        data-testid="chat-mic-button"
        title={voice.title}
        style={{ display: 'flex', alignItems: 'center', flexShrink: 0, padding: '0 10px 0 4px' }}
      />

      {/* No dock-back affordance here: the popped-out editor is a portal of
          this window's editor (FloatingWindow popped mode), and its own ⇥
          pop-in button / closing the OS window brings it home. */}
    </div>
  )
}
